import { toSingaporeTime, formatDateToString } from './dateUtils';

/**
 * Task Time Distribution & Workload Utilization Utilities
 *
 * This module provides utilities for distributing task time across working days
 * and calculating staff workload utilization. Core concepts:
 *
 * - Working Days: Monday-Friday only (weekends excluded)
 * - Daily Capacity: 8 productive hours per staff member (480 minutes)
 * - Time Distribution: Estimated task duration split evenly across working days
 * - Utilization: Percentage of daily capacity used by allocated tasks
 */

// Constants
export const MINUTES_PER_WORKING_DAY = 480; // 8 hours
export const HOURS_PER_WORKING_DAY = 8;

/**
 * Check if a date falls on a working day (Monday-Friday)
 * @param date - Date to check
 * @returns true if Monday-Friday, false if Saturday-Sunday
 */
export const isWorkingDay = (date: Date): boolean => {
  const day = date.getDay();
  return day >= 1 && day <= 5; // Monday=1, Friday=5
};

/**
 * Count working days (Monday-Friday) between two dates, inclusive
 * @param startDate - Start date (inclusive)
 * @param endDate - End date (inclusive)
 * @returns Number of working days
 */
export const getWorkingDaysBetween = (startDate: Date, endDate: Date): number => {
  const start = toSingaporeTime(startDate);
  const end = toSingaporeTime(endDate);

  // Ensure start is before or equal to end
  if (start > end) {
    return 0;
  }

  let workingDays = 0;
  const currentDate = new Date(start);

  while (currentDate <= end) {
    if (isWorkingDay(currentDate)) {
      workingDays++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return workingDays;
};

/**
 * Get array of working day dates between two dates
 * @param startDate - Start date (inclusive)
 * @param endDate - End date (inclusive)
 * @returns Array of Date objects for working days only
 */
export const getWorkingDaysArray = (startDate: Date, endDate: Date): Date[] => {
  const start = toSingaporeTime(startDate);
  const end = toSingaporeTime(endDate);

  const workingDays: Date[] = [];
  const currentDate = new Date(start);

  while (currentDate <= end) {
    if (isWorkingDay(currentDate)) {
      workingDays.push(new Date(currentDate));
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return workingDays;
};

/**
 * Distribute total minutes evenly across a number of days
 * Handles fractional minutes by distributing remainder across first days
 *
 * Example: 185 minutes across 3 days = [62, 62, 61]
 *
 * @param totalMinutes - Total minutes to distribute
 * @param numDays - Number of days to distribute across
 * @returns Array of minutes per day
 */
export const distributeTimeAcrossDays = (totalMinutes: number, numDays: number): number[] => {
  if (numDays <= 0 || totalMinutes <= 0) {
    return [];
  }

  const baseMinutesPerDay = Math.floor(totalMinutes / numDays);
  const remainder = totalMinutes % numDays;

  const distribution: number[] = [];
  for (let i = 0; i < numDays; i++) {
    // Add 1 extra minute to first 'remainder' days
    const minutes = baseMinutesPerDay + (i < remainder ? 1 : 0);
    distribution.push(minutes);
  }

  return distribution;
};

/**
 * Interface for a single day's time allocation
 */
export interface DailyAllocation {
  date: string; // YYYY-MM-DD format
  allocatedMinutes: number;
}

/**
 * Calculate daily time allocations for a task
 * Distributes estimated duration across working days between start and end dates
 *
 * @param startDate - Task start date
 * @param endDate - Task deadline/end date
 * @param estimatedMinutes - Total estimated duration in minutes
 * @returns Array of daily allocations, or empty array if inputs invalid
 */
export const calculateDailyAllocations = (
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
  estimatedMinutes: number | null | undefined
): DailyAllocation[] => {
  // Validate inputs
  if (!startDate || !endDate || !estimatedMinutes || estimatedMinutes <= 0) {
    return [];
  }

  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  // Get working days between dates
  const workingDays = getWorkingDaysArray(start, end);

  if (workingDays.length === 0) {
    return [];
  }

  // Distribute time across working days
  const minutesPerDay = distributeTimeAcrossDays(estimatedMinutes, workingDays.length);

  // Create allocation objects
  const allocations: DailyAllocation[] = workingDays.map((date, index) => ({
    date: formatDateToString(date),
    allocatedMinutes: minutesPerDay[index],
  }));

  return allocations;
};

/**
 * Calculate total minutes allocated for a staff member on a specific date
 *
 * @param allocations - Array of daily allocations
 * @param targetDate - Date to calculate utilization for (string or Date)
 * @returns Total allocated minutes for that date
 */
export const calculateDailyUtilization = (
  allocations: DailyAllocation[],
  targetDate: string | Date
): number => {
  const dateStr = typeof targetDate === 'string'
    ? targetDate
    : formatDateToString(targetDate);

  return allocations
    .filter(allocation => allocation.date === dateStr)
    .reduce((sum, allocation) => sum + allocation.allocatedMinutes, 0);
};

/**
 * Get staff daily capacity in minutes
 * @returns 480 minutes (8 hours)
 */
export const getStaffDailyCapacity = (): number => {
  return MINUTES_PER_WORKING_DAY;
};

/**
 * Calculate utilization percentage
 *
 * @param usedMinutes - Minutes allocated/used
 * @param capacityMinutes - Total capacity in minutes (default: 480)
 * @returns Utilization percentage (0-100+, can exceed 100)
 */
export const getUtilizationPercentage = (
  usedMinutes: number,
  capacityMinutes: number = MINUTES_PER_WORKING_DAY
): number => {
  if (capacityMinutes <= 0) {
    return 0;
  }
  return Math.round((usedMinutes / capacityMinutes) * 100);
};

/**
 * Get utilization status color based on percentage
 *
 * @param percentage - Utilization percentage
 * @returns Color classification
 */
export const getUtilizationColor = (percentage: number): 'green' | 'yellow' | 'orange' | 'red' => {
  if (percentage > 100) return 'red';
  if (percentage >= 80) return 'orange';
  if (percentage >= 50) return 'yellow';
  return 'green';
};

/**
 * Format minutes as human-readable duration
 *
 * @param minutes - Duration in minutes
 * @returns Formatted string (e.g., "2h 30m", "1.5h", "45m")
 */
export const formatMinutesToDuration = (minutes: number): string => {
  if (minutes < 60) {
    return `${minutes}m`;
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (mins === 0) {
    return `${hours}h`;
  }

  // For display, show decimal for common fractions
  if (mins === 30) {
    return `${hours}.5h`;
  }

  return `${hours}h ${mins}m`;
};

/**
 * Validate task dates and duration for allocation
 *
 * @param startDate - Task start date
 * @param endDate - Task end date
 * @param estimatedMinutes - Estimated duration
 * @returns Validation result with message
 */
export const validateTaskForAllocation = (
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
  estimatedMinutes: number | null | undefined
): { valid: boolean; message?: string } => {
  if (!startDate || !endDate || !estimatedMinutes) {
    return { valid: false, message: 'Missing required fields for time allocation' };
  }

  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  if (start > end) {
    return { valid: false, message: 'Start date must be before or equal to deadline' };
  }

  const workingDays = getWorkingDaysBetween(start, end);

  if (workingDays === 0) {
    return {
      valid: false,
      message: 'Task spans only weekends - no working days available for allocation'
    };
  }

  const totalCapacity = workingDays * MINUTES_PER_WORKING_DAY;
  if (estimatedMinutes > totalCapacity) {
    const hoursNeeded = Math.ceil(estimatedMinutes / 60);
    const hoursAvailable = (workingDays * HOURS_PER_WORKING_DAY);
    return {
      valid: true, // Warning, not error
      message: `Warning: Task requires ${hoursNeeded}h but only ${hoursAvailable}h available across ${workingDays} working days (exceeds 8h/day capacity)`
    };
  }

  return { valid: true };
};

/**
 * Get allocation preview text for UI display
 *
 * @param startDate - Task start date
 * @param endDate - Task end date
 * @param estimatedMinutes - Estimated duration
 * @returns Human-readable preview string
 */
export const getAllocationPreview = (
  startDate: Date | string | null | undefined,
  endDate: Date | string | null | undefined,
  estimatedMinutes: number | null | undefined
): string | null => {
  const allocations = calculateDailyAllocations(startDate, endDate, estimatedMinutes);

  if (allocations.length === 0) {
    return null;
  }

  const avgMinutes = Math.round(
    allocations.reduce((sum, a) => sum + a.allocatedMinutes, 0) / allocations.length
  );

  const avgDuration = formatMinutesToDuration(avgMinutes);
  const numDays = allocations.length;

  return `This task will allocate approximately ${avgDuration} per day across ${numDays} working day${numDays !== 1 ? 's' : ''}`;
};
